/**
 * File: LegalResearchMemoDashboard.jsx
 * Path: /src/components/solutions/LegalResearchMemoDashboard.jsx
 * Description: Standalone legal memo visualization for Legal Research AI solution
 * Key Features: Interactive display of AI-generated legal memo with citation highlights
 */

import React from 'react';
import { motion } from 'framer-motion';

const LegalResearchMemoDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-primary-100 to-secondary-100 p-1">
        <div className="bg-white rounded-lg p-6 relative">
          {/* Document Analysis Visualization */}
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="document-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#document-pattern)" />
            </svg>
          </div>
          
          {/* Dashboard container with min-height to prevent cutting off */}
          <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
            <div className="w-full">
              {/* Mockup content */}
              <div className="flex flex-col h-full relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-500 rounded-md flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <span className="font-semibold text-dark-600">AI-Generated Legal Memo</span>
                  </div>
                  <div className="flex space-x-3">
                    <div className="py-1 px-3 text-xs bg-primary-50 text-primary-600 rounded-full">Drafting</div>
                    <div className="py-1 px-3 text-xs bg-green-50 text-green-600 rounded-full">85% Complete</div>
                  </div>
                </div>
                
                {/* Document preview with highlights */}
                <div className="flex-grow grid grid-cols-5 gap-6">
                  <div className="col-span-3 border border-dark-100 rounded-lg p-4 text-sm text-dark-700">
                    <div className="text-lg font-bold mb-3">Memorandum of Law</div>
                    <div className="mb-2">RE: Enforceability of Non-Compete Agreements in California</div>
                    <div className="mb-4">DATE: May 4, 2025</div>
                    
                    <p className="mb-3">California law generally prohibits non-compete agreements under <span className="bg-yellow-100 rounded px-1">Business and Professions Code Section 16600</span>, which states that "every contract by which anyone is restrained from engaging in a lawful profession, trade, or business of any kind is to that extent void."</p>
                    
                    <p className="mb-3">The California Supreme Court's decision in <span className="bg-primary-100 rounded px-1">AMN Services, LLC v. Aya Healthcare Services, Inc.</span> (2018) reaffirmed this prohibition, holding that Section 16600 represents a "settled legislative policy in favor of open competition and employee mobility."</p>
                    
                    <p className="mb-3">However, there are limited exceptions to this rule:</p>
                    
                    <ol className="list-decimal pl-6 mb-3 space-y-1">
                      <li>Sale of business (<span className="bg-yellow-100 rounded px-1">Bus. & Prof. Code § 16601</span>)</li>
                      <li>Dissolution of partnership (<span className="bg-yellow-100 rounded px-1">Bus. & Prof. Code § 16602</span>)</li>
                      <li>Dissolution of LLC (<span className="bg-yellow-100 rounded px-1">Bus. & Prof. Code § 16602.5</span>)</li>
                    </ol>
                  </div>
                  
                  <div className="col-span-2 flex flex-col space-y-4">
                    <div className="bg-primary-50 rounded-lg p-4 flex-1">
                      <div className="text-primary-600 font-semibold mb-2 text-sm">Key Cases</div>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">1</div>
                          <div>
                            <div className="font-medium">AMN Services, LLC v. Aya Healthcare Services, Inc.</div>
                            <div className="text-dark-500 text-xs">California Supreme Court (2018)</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">2</div>
                          <div>
                            <div className="font-medium">Edwards v. Arthur Andersen LLP</div>
                            <div className="text-dark-500 text-xs">California Supreme Court (2008)</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">3</div>
                          <div>
                            <div className="font-medium">Dowell v. Biosense Webster, Inc.</div>
                            <div className="text-dark-500 text-xs">California Court of Appeal (2009)</div>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-accent-50 rounded-lg p-4 flex-1">
                      <div className="text-accent-600 font-semibold mb-2 text-sm">Key Statutes</div>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mt-0.5 mr-2 text-xs">§</div>
                          <div>
                            <div className="font-medium">Cal. Bus. & Prof. Code § 16600</div>
                            <div className="text-dark-500 text-xs">Prohibition on restraints</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mt-0.5 mr-2 text-xs">§</div>
                          <div>
                            <div className="font-medium">Cal. Bus. & Prof. Code § 16601</div>
                            <div className="text-dark-500 text-xs">Sale of business exception</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mt-0.5 mr-2 text-xs">§</div>
                          <div>
                            <div className="font-medium">Cal. Bus. & Prof. Code § 16602</div>
                            <div className="text-dark-500 text-xs">Partnership dissolution</div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalResearchMemoDashboard;